import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import { TradingSpreadKind } from '@ebalance/proto/bfftypes/v1/trading_spread_kind_pb'
import { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import {
  formatDateValue,
  fromProtoDate,
  parseIsoDate,
  toProtoDate,
} from '~/models/dateTime'
import {
  bool,
  calendarDate,
  defineFilterCodec,
  nonEmptyText,
  oneOf,
  optional,
  protoEnumValues,
} from '~/models/filterCodec'
import type { FilterState } from '~/pages/SupplyBilateral/hooks/useFilterState'

/**
 * 取引契約管理のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定する。
 *
 * この画面は行から契約詳細モーダル、および 48点データへ route 遷移する
 * (`models/supply48Link.ts` の `buildSupply48Url`)。保持しないと戻るたびに
 * フィルタの選択状態がすべて初期値へ戻る。
 *
 * [規約逸脱の理由 = key を `-v2` へ上げた理由] 旧 key (`-v2` なし) の保存値は、
 * 旧 serializer が「利用者がステータスに触っていなくても `statusFilter: 2`
 * (= 成立済) を毎回書く」形式だった。現行の復元はこれを「利用者が明示的に成立済を
 * 選んだ」と読むため、旧 key のまま読むと AC-SBL-015 の既定 (= すべて) が
 * 既存端末に届かない。保存済みフィルタを一度だけ破棄するため key を上げる
 * (2026-09-05 ユーザー判断)。versioned key の書式は `~/hooks/useGridCardLayout`
 * の `ebalance-doji-bg-layout-v1` / `ebalance-hatsu-layout-v1` に揃える。
 * 旧 key の明示削除は行わない。同 hook にも **旧 version key を掃除する処理は
 * 無い** — `clearGridCardLayouts()` が消すのは現行の 2 キーで、
 * 目的も version 移行ではなくログアウト時の破棄。同 hook の NOTE が follow-up と
 * しているのも「ログアウト後に残る `ebalance-` prefix キーの破棄方針」であって、
 * version 移行時の旧 key 掃除とは別の関心事。
 */
export const SUPPLY_BILATERAL_FILTERS_STORAGE_KEY =
  'ebalance-supply-bilateral-filters-v2'

/**
 * 保持対象のフィルタ (= `FilterState` から `page` / `pageSize` を除いたもの)。
 *
 * ## ページャを保持しない理由
 *
 * `page` はフィルタではなく**位置**で、`setFilter` はフィルタ変更のたびに 1 へ
 * 戻す設計 (`useFilterState`)。復元しても件数が変わっていれば同じページに同じ行は
 * 無く、空ページを表示しかねない。`pageSize` は表示密度の設定だが UI に変更手段が
 * 無いため (常に既定の 50)、保持する意味が無い。
 *
 * ## 相対先は自由入力ではない
 *
 * `partnerNameFilter` は文字列だが UI は DS `Select` (取引先候補からの単一選択) で、
 * AC-SC-031 §6 機能詳細 2 の「単一選択」に当たる。広域マスタの検索テキスト
 * (自由入力なので対象外と判断) とは別物。
 */
export type SupplyBilateralFilters = Omit<FilterState, 'page' | 'pageSize'>

/**
 * フィルタ既定値 (= 初期表示と「クリア」の戻り先)。
 *
 * AC-SBL-015 [P0] が「全フィルタを初期値 (すべて選択) で適用し、 取引データを
 * 取得・表示する」と定めるため、 ステータスを含む全フィルタが絞り込みなし。
 * 「期間満了も含む」 だけは spec のフィルタ表が既定 OFF と明示している
 * (= 受渡期間が終了した契約を一覧から外した状態が既定ビュー。 design
 * `design/supply/bilateral.html` の `bi-status` すべて + `bi-show-expired` OFF と同じ)。
 * 時計に依存しない。
 */
export function defaultSupplyBilateralFilters(): SupplyBilateralFilters {
  return {
    buySellFilter: undefined,
    executionRouteFilter: undefined,
    timeZoneTypeFilter: undefined,
    deliveryTypeFilter: undefined,
    areaCodeFilter: undefined,
    partnerNameFilter: undefined,
    deliveryPeriodFrom: undefined,
    deliveryPeriodTo: undefined,
    statusFilter: undefined,
    spreadKindFilter: undefined,
    includeExpired: false,
  }
}

const protoDate = calendarDate<ProtoDate>({
  parse: (ymd) => {
    const parsed = parseIsoDate(ymd)
    return parsed === undefined ? undefined : toProtoDate(parsed)
  },
  format: (d) => formatDateValue(fromProtoDate(d)),
})

/**
 * 保存形式は項目名 = `FilterState` の項目名、 enum は数値、 受渡期間は暦日
 * `YYYY-MM-DD` 文字列 (proto message の内部表現に保存形式を縛られない)。
 * `undefined` はキーごと落ち、復元側は「キーが無い」を「絞り込みなし」と読む。
 *
 * **項目ごとに独立して落とす** (all-or-nothing にしない)。1 項目の破損で
 * 「ほとんど正しい保存値」を丸ごと捨てない。
 *
 * 時間帯だけ `UNSPECIFIED` (0) も有効値に含める例外 (AC-SBL-047、issue #6698、
 * 2026-09-04 PdM判断)。他の enum は `UNSPECIFIED` を除外し「すべて」= `undefined` に
 * 丸めるが、時間帯フィルタだけは「フィールドあり + UNSPECIFIED」が「カスタム」という
 * 別の意味を持つため、0 を復元対象に含めて `undefined` (絞り込みなし) と区別する。
 *
 * ステータスは proto の全値 (成立済 / ドラフト / 期間満了 / 取消) を復元対象にする。
 * spec AC-SBL-021 のフィルタ表は「ドラフトを含まず」 実装 select は含む (既存踏襲、
 * #4795 で Des / PdM 判断待ち、`components/FilterBar/index.tsx` の [design差異] 参照)
 * ため、判断が出るまでは復元側も画面で選べる値を捨てない側に倒す。
 *
 * **取引先候補との突き合わせはしない。** 候補はサーバー由来で純関数からは見えない。
 * 候補外の相対先が復元されると 0 件になるが、`Select` は選択中の値を表示するので
 * 画面上は絞り込みが見える。
 */
export const supplyBilateralFiltersCodec =
  defineFilterCodec<SupplyBilateralFilters>({
    defaults: defaultSupplyBilateralFilters,
    fields: {
      buySellFilter: optional(oneOf<Side>([Side.SELL, Side.BUY])),
      executionRouteFilter: optional(
        oneOf(protoEnumValues(TradingExecutionRoute)),
      ),
      timeZoneTypeFilter: optional(
        oneOf(
          protoEnumValues(TradingTimeZoneType, { includeUnspecified: true }),
        ),
      ),
      deliveryTypeFilter: optional(oneOf(protoEnumValues(TradingDeliveryType))),
      areaCodeFilter: optional(oneOf<AreaCode>(AREA_CODE_OPTIONS)),
      partnerNameFilter: optional(nonEmptyText()),
      deliveryPeriodFrom: optional(protoDate),
      deliveryPeriodTo: optional(protoDate),
      statusFilter: optional(oneOf(protoEnumValues(TradingContractStatus))),
      spreadKindFilter: optional(oneOf(protoEnumValues(TradingSpreadKind))),
      includeExpired: bool(),
    },
  })

/**
 * 詳細フィルタ (受渡区分 / スプレッド種別 / 時間帯 / 約定経路 / 相対先) のいずれかが
 * 効いているか。
 *
 * **開閉トグル自体は保持しない。** 表示のトグルであってフィルタの選択状態ではなく、
 * 48点データのグラフ表示トグルと同じ扱い。ただし値だけ復元して折りたたんだままだと
 * **「絞り込まれているのに理由が画面から見えない」**状態になるので、値が入っていれば
 * 開いた状態で始める (= 保存するのではなく値から導く)。
 */
export function hasActiveDetailFilters(
  filters: SupplyBilateralFilters,
): boolean {
  return (
    filters.deliveryTypeFilter !== undefined ||
    filters.spreadKindFilter !== undefined ||
    filters.timeZoneTypeFilter !== undefined ||
    filters.executionRouteFilter !== undefined ||
    filters.partnerNameFilter !== undefined
  )
}
